import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { INR } from "@/lib/constants";
import { useConfirm } from "@/context/ConfirmContext";
import { Pagination, usePagination } from "@/components/Pagination";
import QuoteBuilder from "@/components/quotes/QuoteBuilder";
import QuoteSettings from "@/components/quotes/QuoteSettings";
import QuoteReferences from "@/components/quotes/QuoteReferences";
import { Plus, FileText, MessageCircle, Link2, Pencil, Trash2, Search, Settings2, ScrollText, Handshake, Wallet, X, Inbox, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODE_BADGE = {
  direct: "bg-[#C88270]/10 text-[#C88270] border-[#C88270]/30",
  partner: "bg-[#7A9E9F]/10 text-[#4f6f70] border-[#7A9E9F]/40",
};

const PAYMENT_META = {
  unpaid: { label: "Unpaid", cls: "bg-[#FAF3F0] text-[#B06A54] border-[#E4C9C0]" },
  advance_received: { label: "Advance received", cls: "bg-[#FBF6EC] text-[#9A7B33] border-[#E7DBB8]" },
  paid: { label: "Paid", cls: "bg-[#EEF5EE] text-[#4B7A4E] border-[#CADFCB]" },
};

const PAGE_SIZE = 12;

export default function QuotationStudioPage() {
  const confirm = useConfirm();
  const [tab, setTab] = useState("quotes");
  const [settings, setSettings] = useState(null);
  const [references, setReferences] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState("all");
  const [payStatus, setPayStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payQuote, setPayQuote] = useState(null);
  const [seed, setSeed] = useState(null);
  const [seedEnquiryId, setSeedEnquiryId] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const pendingCount = enquiries.filter((e) => e.status !== "quoted").length;

  const loadEnquiries = () => api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  useEffect(() => { loadEnquiries(); }, []);

  useEffect(() => { api.get("/quote-settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/quote-references").then((r) => setReferences(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setDebounced(search), 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [mode, debounced, payStatus, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE, search: debounced };
      if (mode !== "all") params.mode = mode;
      if (payStatus !== "all") params.payment_status = payStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/quotes", { params });
      setQuotes(data.items || []); setTotal(data.total || 0); setTotalPages(data.total_pages || 1);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, mode, debounced, payStatus, dateFrom, dateTo]);
  useEffect(() => { if (tab === "quotes") load(); }, [load, tab]);

  const openNew = () => { setEditing(null); setSeed(null); setSeedEnquiryId(null); setBuilderOpen(true); };
  const openEdit = (q) => { setEditing(q); setSeed(null); setSeedEnquiryId(null); setBuilderOpen(true); };
  const quoteFromEnquiry = (e) => {
    setEditing(null);
    setSeed({ client_name: e.client_name, client_phone: e.client_phone, events: e.events || [] });
    setSeedEnquiryId(e.id);
    setTab("quotes");
    setBuilderOpen(true);
  };
  const onBuilderSaved = async () => {
    if (seedEnquiryId) { try { await api.post(`/enquiries/${seedEnquiryId}/mark-quoted`); } catch { /* non-blocking */ } loadEnquiries(); }
    setSeed(null); setSeedEnquiryId(null);
    load();
  };
  const copyEnquiryLink = () => copyText(`${window.location.origin}/enquiry`, "Enquiry link copied");
  const delEnquiry = async (id) => {
    const ok = await confirm({ title: "Remove enquiry?", description: "This enquiry will be permanently deleted.", confirmText: "Remove", destructive: true });
    if (!ok) return;
    try { await api.delete(`/enquiries/${id}`); loadEnquiries(); toast.success("Enquiry removed"); } catch (err) { toast.error(formatApiError(err)); }
  };

  const shareUrl = (q) => `${window.location.origin}/api/public/quotes/${q.public_id}/share`;
  const copyText = async (text, msg = "Copied to clipboard") => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); ok = true; }
    } catch (err) { console.warn("clipboard API copy failed, falling back to execCommand", err); }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.top = "0"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (err) { console.warn("execCommand copy fallback failed", err); ok = false; }
    }
    if (ok) toast.success(msg);
    else toast.error("Couldn't auto-copy. Long-press/select to copy: " + text);
  };
  const copyLink = (q) => copyText(shareUrl(q), "Shareable link copied — shows client name & date in WhatsApp preview");
  const shareWhatsApp = (q) => {
    const text = `Hi ${q.client_name}, here is your personalised quotation from ${settings?.studio?.name || "Dejavu"}:\n\n${shareUrl(q)}\n\nTotal: ${INR(q.calc.total)}${q.calc.advance_amount ? ` · Advance to block date: ${INR(q.calc.advance_amount)}` : ""}`;
    const phone = (q.client_phone || "").replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };
  const openPdf = (q) => window.open(`${BACKEND_URL}/api/quotes/${q.id}/pdf`, "_blank");
  const remove = async (q) => {
    const ok = await confirm({ title: "Delete quotation?", description: `Delete the quote for ${q.client_name}? This cannot be undone.`, confirmText: "Delete", destructive: true });
    if (!ok) return;
    try { await api.delete(`/quotes/${q.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="quotation-studio">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C88270] mb-2">Quotation Studio</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#2A2421] leading-none">Beautiful Quotes</h1>
          <p className="text-sm text-[#5C5652] mt-2">Craft designed quotations for direct clients & event partners.</p>
        </div>
        {tab === "quotes" && (
          <button onClick={openNew} data-testid="new-quote-btn"
            className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.2em] bg-[#2A2421] text-white hover:bg-[#C88270] transition-colors">
            <Plus size={16} /> New Quotation
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-[#EAE7E0] mb-6 overflow-x-auto">
        {[["quotes", "Quotations", ScrollText], ["enquiries", "Enquiries", Inbox], ["references", "References", Handshake], ["settings", "Settings", Settings2]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === k ? "border-[#C88270] text-[#2A2421]" : "border-transparent text-[#5C5652] hover:text-[#2A2421]"}`}>
            <Icon size={15} /> {label}
            {k === "enquiries" && pendingCount > 0 && (
              <span data-testid="enquiries-count" className="ml-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-[#C88270] text-white text-[10px] tracking-normal">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "settings" ? (
        <QuoteSettings settings={settings} onChanged={setSettings} />
      ) : tab === "references" ? (
        <QuoteReferences onChanged={setReferences} />
      ) : tab === "enquiries" ? (
        <EnquiriesTab enquiries={enquiries} onCopyLink={copyEnquiryLink} onCreateQuote={quoteFromEnquiry} onDelete={delEnquiry} />
      ) : (
        <>
          <div className="flex flex-col gap-3 mb-5">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5652]" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="quote-search" placeholder="Search by name or phone…"
                  className="w-full pl-10 pr-3 py-2.5 bg-white border border-[#EAE7E0] text-sm focus:border-[#C88270] focus:outline-none" />
              </div>
              <div className="flex gap-1">
                {[["all", "All"], ["direct", "Direct"], ["partner", "Partner"]].map(([k, l]) => (
                  <button key={k} onClick={() => setMode(k)} data-testid={`filter-${k}`}
                    className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors ${mode === k ? "bg-[#2A2421] text-white border-[#2A2421]" : "bg-white border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421]"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <select value={payStatus} onChange={(e) => setPayStatus(e.target.value)} data-testid="quote-payment-filter"
                className="px-3 py-2.5 bg-white border border-[#EAE7E0] text-sm text-[#5C5652] focus:border-[#C88270] focus:outline-none">
                <option value="all">All payments</option>
                <option value="unpaid">Unpaid</option>
                <option value="advance_received">Advance received</option>
                <option value="paid">Paid</option>
              </select>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#5C5652]">Created</span>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="quote-date-from"
                  className="px-3 py-2.5 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none" />
                <span className="text-[#B8B2AA] text-xs">—</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="quote-date-to"
                  className="px-3 py-2.5 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none" />
              </div>
              {(payStatus !== "all" || dateFrom || dateTo) && (
                <button onClick={() => { setPayStatus("all"); setDateFrom(""); setDateTo(""); }} data-testid="quote-clear-filters"
                  className="text-[10px] uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421]">Clear filters</button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-[#5C5652] text-xs uppercase tracking-[0.3em]">Loading</div>
          ) : total === 0 ? (
            <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]" data-testid="no-quotes">
              No quotations yet. Click “New Quotation” to create your first.
            </div>
          ) : (
            <div className="space-y-3">
              {quotes.map((q) => (
                <div key={q.id} data-testid={`quote-card-${q.id}`}
                  className="bg-white border border-[#EAE7E0] p-5 flex flex-col md:flex-row md:items-center gap-4 hover:border-[#C88270]/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif text-xl text-[#2A2421]">{q.client_name}</span>
                      <span className={`text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 border rounded-full ${MODE_BADGE[q.mode] || MODE_BADGE.direct}`}>{q.mode}</span>
                      <span data-testid={`payment-badge-${q.id}`} className={`text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 border rounded-full ${(PAYMENT_META[q.payment_status] || PAYMENT_META.unpaid).cls}`}>{(PAYMENT_META[q.payment_status] || PAYMENT_META.unpaid).label}</span>
                    </div>
                    <div className="text-xs text-[#5C5652] mt-1">
                      {(q.events || []).length} event{(q.events || []).length !== 1 ? "s" : ""}
                      {" · "}Total {INR(q.calc.total)}
                      {q.calc.advance_amount > 0 && <> · Advance {INR(q.calc.advance_amount)}</>}
                      {q.amount_received > 0 && <> · <span className="text-[#4B7A4E]">Received {INR(q.amount_received)}</span></>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <ActionBtn onClick={() => openEdit(q)} icon={Pencil} label="Edit" testId={`edit-${q.id}`} />
                    <ActionBtn onClick={() => setPayQuote(q)} icon={Wallet} label="Payment" testId={`payment-${q.id}`} />
                    <ActionBtn onClick={() => openPdf(q)} icon={FileText} label="PDF" testId={`pdf-${q.id}`} />
                    <ActionBtn onClick={() => shareWhatsApp(q)} icon={MessageCircle} label="WhatsApp" testId={`whatsapp-${q.id}`} />
                    <ActionBtn onClick={() => copyLink(q)} icon={Link2} label="Link" testId={`link-${q.id}`} />
                    <ActionBtn onClick={() => remove(q)} icon={Trash2} label="" testId={`delete-${q.id}`} danger />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && total > 0 && (
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} testId="quotes-pagination" />
          )}
        </>
      )}

      <QuoteBuilder open={builderOpen} onClose={() => setBuilderOpen(false)} onSaved={onBuilderSaved} settings={settings} references={references} quote={editing} seed={seed} />
      <PaymentModal quote={payQuote} onClose={() => setPayQuote(null)} onSaved={() => load()} />
    </div>
  );
}

function PaymentModal({ quote, onClose, onSaved }) {
  const [status, setStatus] = useState("unpaid");
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (quote) { setStatus(quote.payment_status || "unpaid"); setAmount(quote.amount_received || 0); setNote(quote.payment_note || ""); }
  }, [quote]);
  if (!quote) return null;

  const quickFill = (v) => setAmount(v);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/quotes/${quote.id}/payment`, { payment_status: status, amount_received: Number(amount) || 0, payment_note: note });
      toast.success("Payment updated");
      onSaved?.(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="payment-modal">
      <div className="bg-[#FAF9F6] w-full max-w-md border border-[#EAE7E0] shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EAE7E0]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C88270]">Record Payment</div>
            <div className="font-serif text-xl text-[#2A2421]">{quote.client_name}</div>
          </div>
          <button onClick={onClose} data-testid="payment-close" className="p-1 text-[#5C5652] hover:text-[#2A2421]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-[#5C5652]">Total {INR(quote.calc.total)}{quote.calc.advance_amount > 0 && <> · Advance {INR(quote.calc.advance_amount)}</>}</div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#5C5652] mb-2">Payment status</label>
            <div className="flex gap-2">
              {[["unpaid", "Unpaid"], ["advance_received", "Advance"], ["paid", "Paid"]].map(([v, l]) => (
                <button key={v} onClick={() => setStatus(v)} data-testid={`pay-status-${v}`}
                  className={`flex-1 px-3 py-2 text-xs uppercase tracking-[0.12em] border transition-colors ${status === v ? "bg-[#2A2421] text-white border-[#2A2421]" : "bg-white border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#5C5652] mb-1.5">Amount received (₹)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" className={inputCls} />
            <div className="flex gap-2 mt-2">
              {quote.calc.advance_amount > 0 && <button onClick={() => quickFill(quote.calc.advance_amount)} className="text-[10px] uppercase tracking-[0.12em] text-[#C88270] hover:text-[#2A2421]">Advance {INR(quote.calc.advance_amount)}</button>}
              <button onClick={() => quickFill(quote.calc.total)} className="text-[10px] uppercase tracking-[0.12em] text-[#C88270] hover:text-[#2A2421]">Full {INR(quote.calc.total)}</button>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#5C5652] mb-1.5">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="pay-note" placeholder="e.g. UPI on 12 Jun, GPay ref…" className={inputCls} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#EAE7E0]">
          <button onClick={onClose} className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="pay-save" className="px-6 py-2.5 text-xs uppercase tracking-[0.2em] bg-[#2A2421] text-white hover:bg-[#C88270] transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testId, danger }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.15em] border transition-colors ${danger ? "border-[#EAE7E0] text-[#5C5652] hover:border-red-400 hover:text-red-600" : "border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421]"}`}>
      <Icon size={14} />{label}
    </button>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
}

function EnquiriesTab({ enquiries, onCopyLink, onCreateQuote, onDelete }) {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const filtered = enquiries.filter((e) => {
    if (status === "quoted" && e.status !== "quoted") return false;
    if (status === "pending" && e.status === "quoted") return false;
    if (q.trim() && !`${e.client_name} ${e.client_phone}`.toLowerCase().includes(q.trim().toLowerCase())) return false;
    if (from || to) {
      const dates = (e.events || []).map((ev) => ev.date).filter(Boolean);
      if (!dates.some((d) => (!from || d >= from) && (!to || d <= to))) return false;
    }
    return true;
  });
  const { page, setPage, totalPages, pageItems, total, pageSize } = usePagination(filtered, 10, `${status}|${q}|${from}|${to}`);

  return (
    <div data-testid="enquiries-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 bg-white border border-[#EAE7E0] p-5">
        <div>
          <div className="font-serif text-lg text-[#2A2421]">Public enquiry form</div>
          <p className="text-xs text-[#5C5652] mt-1">Share this link with clients & partners to collect their event details.</p>
        </div>
        <button onClick={onCopyLink} data-testid="copy-enquiry-link"
          className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] bg-[#2A2421] text-white hover:bg-[#C88270] transition-colors whitespace-nowrap">
          <Link2 size={15} /> Copy Enquiry Link
        </button>
      </div>

      <div className="flex flex-col gap-3 mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5652]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="enquiry-search" placeholder="Search by name or phone…"
              className="w-full pl-10 pr-3 py-2.5 bg-white border border-[#EAE7E0] text-sm focus:border-[#C88270] focus:outline-none" />
          </div>
          <div className="flex gap-1">
            {[["all", "All"], ["pending", "Pending"], ["quoted", "Quoted"]].map(([k, l]) => (
              <button key={k} onClick={() => setStatus(k)} data-testid={`enquiry-filter-${k}`}
                className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors ${status === k ? "bg-[#2A2421] text-white border-[#2A2421]" : "bg-white border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#5C5652]">Event date</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="enquiry-date-from"
            className="px-3 py-2.5 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none" />
          <span className="text-[#B8B2AA] text-xs">—</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="enquiry-date-to"
            className="px-3 py-2.5 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none" />
          {(from || to || q || status !== "all") && (
            <button onClick={() => { setFrom(""); setTo(""); setQ(""); setStatus("all"); }} data-testid="enquiry-clear-filters" className="text-[10px] uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421]">Clear</button>
          )}
        </div>
      </div>

      {total === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]" data-testid="no-enquiries">
          {enquiries.length === 0 ? "No enquiries yet. Share the enquiry link to start receiving event details." : "No enquiries match these filters."}
        </div>
      ) : (
        <div className="space-y-3">
          {pageItems.map((e) => (
            <div key={e.id} data-testid={`enquiry-card-${e.id}`}
              className="bg-white border border-[#EAE7E0] p-5 flex flex-col md:flex-row md:items-start gap-4 hover:border-[#C88270]/50 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-xl text-[#2A2421]">{e.client_name}</span>
                  {e.status === "quoted"
                    ? <span className="text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 border rounded-full bg-[#EEF5EE] text-[#4B7A4E] border-[#CADFCB]">Quoted</span>
                    : <span className="text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 border rounded-full bg-[#FBF6EC] text-[#9A7B33] border-[#E7DBB8]">Pending</span>}
                  <span className="text-[10px] text-[#B8B2AA]">{fmtDate(e.created_at)}</span>
                </div>
                <a href={`tel:${e.client_phone}`} className="inline-flex items-center gap-1.5 text-xs text-[#5C5652] mt-1 hover:text-[#C88270]">
                  <Phone size={12} /> {e.client_phone}
                </a>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(e.events || []).map((ev, i) => (
                    <div key={i} className="text-xs bg-[#FAF3F0] border border-[#E4C9C0] px-3 py-1.5">
                      <span className="text-[#2A2421] font-medium">{ev.event_type}</span>
                      {ev.date && <span className="text-[#5C5652]"> · {fmtDate(ev.date)}</span>}
                      {ev.slots?.length > 0 && <span className="text-[#5C5652]"> · {ev.slots.join(", ")}</span>}
                      {ev.location && <span className="text-[#5C5652]"> · {ev.location}</span>}
                      {ev.ready_by && <span className="text-[#5C5652]"> · ready {ev.ready_by}</span>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <ActionBtn onClick={() => onCreateQuote(e)} icon={FileText} label="Create Quote" testId={`enquiry-quote-${e.id}`} />
                <ActionBtn onClick={() => onDelete(e.id)} icon={Trash2} label="" testId={`enquiry-del-${e.id}`} danger />
              </div>
            </div>
          ))}
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onChange={setPage} testId="enquiries-pagination" />
        </div>
      )}
    </div>
  );
}